<template>
  <base-dialog :value="isVisibleValue"
               hideBtn
               title="Install the chat on your site"
               variant="base"
               @cancel="isVisibleValue = false"

  >
    <template #prompt-input>
      <div class="text-sm text-gray-dark">
        <div>
          Insert these code snippets before their respective closing tags on every page you want
          the chat to appear.
        </div>
        <div class="mt-2">
          Your chat theme and text settings are applied from this app. Any change you save and
          deploy is propagated to this script automatically — you do not need to update the
          snippet when you edit a theme. Manage themes from the bot settings menu.
        </div>
      </div>

      <BaseToggleGroup class="mt-4" :tabs="TAB_OPTIONS" v-model="activeTab" />

      <div class="mt-4">
        <SettingsBotSnippetJavaScript v-if="activeTab === 'js'"
                                      :head-code-snippet-text="HEAD_CODE_SNIPPET_TEXT"
                                      :body-code-snippet-text="bodyCodeSnippetText"
                                      @go-to-theme="goToTheme"
        />
        <SettingsBotSnippetGoogleTagManager v-else
                                      :widget-url="WIDGET_URL"
                                      :code-snippet-text="googleTagManagerCodeSnippetText"
                                      @go-to-theme="goToTheme"
        />
      </div>
    </template>
  </base-dialog>
</template>

<script>
import { ROUTE_NAMES } from '@/router'
import SettingsBotSnippetJavaScript from './SettingsBotSnippetJavaScript.vue'
import SettingsBotSnippetGoogleTagManager from './SettingsBotSnippetGoogleTagManager.vue'

const WIDGET_URL = 'https://chat-widget.static-amio.com/static/amio-webchat-loader.min.js'
const HEAD_CODE_SNIPPET_TEXT = `<script src="${WIDGET_URL}" type="text/javascript"></` + 'script>'
const TAB_OPTIONS = Object.freeze([
  { key: 'js', label: 'JavaScript' },
  { key: 'gtm', label: 'Google Tag Manager' }
])

export default {
  name: 'SettingsBotSnippet',

  components: { SettingsBotSnippetJavaScript, SettingsBotSnippetGoogleTagManager },

  props: {
    isVisible: {
      type: Boolean,
      default: false
    },
    bot: {
      type: Object,
      required: true
    },
    selectedChannel: {
      type: Object,
      default: () => {}
    },
    selectedTheme: {
      type: Object,
      default: null
    }
  },

  data() {
    return {
      isHeadCodeSnippetCopied: false,
      isBodyCodeSnippetCopied: false,
      ROUTE_NAMES,
      HEAD_CODE_SNIPPET_TEXT,
      WIDGET_URL,
      TAB_OPTIONS,
      activeTab: 'js'
    }
  },

  emits: ['update:is-visible'],

  computed: {
    isVisibleValue: {
      get() {
        return this.isVisible
      },
      set(value) {
        this.$emit('update:is-visible', value)
      }
    },

    bodyCodeSnippetText() {
      return `<script type="text/javascript">
  window.amioWebchat.init({
    // All options are listed at https://docs.amio.io/docs/amio-chat-api-methods#init
    channelId: '${this.selectedChannel.id}'
  })
</` + 'script>'
    },

    googleTagManagerCodeSnippetText() {
      return `<script>
  (function(d, s, id) {
    if (d.getElementById(id)) return;
    var js = d.createElement(s);
    js.async = true;
    js.src = "https://chat-widget.static-amio.com/static/amio-webchat-loader.min.js";
    js.id = id;
    js.onload = function () {
      window.amioWebchat.init({
        // All options are listed at https://docs.amio.io/docs/amio-chat-api-methods#init
        channelId: '${this.selectedChannel.id}'
      });
    };
    d.head.appendChild(js);
  }) (document, 'script', 'amio-webchat');
</` + 'script>'
    }
  },

  methods: {
    async goToTheme() {
      this.isVisibleValue = false
      await this.$nextTick()
      this.$router.push({
        name: ROUTE_NAMES.botTheme,
        params: { chatThemeId: this.selectedTheme.id }
      })
    }
  }
}
</script>
